import {
  DiagnosticRemoteAssistanceCustomerDto,
  DiagnosticRemoteAssistanceVehicleDto,
} from '@services/api/api.service';
import { RemoteServiceConsentDto, RemoteServiceListItemDto } from '@services/api/sdf-remote-services-api.service';
import { createReducer, on } from '@ngrx/store';
import * as RootActions from '@redux/actions';
import * as Actions from '@redux/modules/remote-services/dtech-remote/actions';

export interface State {
  // --------------------------------
  // DEALER D-TECH REMOTE
  // --------------------------------
  dTechRemoteAssistances: {
    data: RemoteServiceListItemDto[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    };
    filters: {
      runningOnly?: boolean;
      connectedOnly?: boolean;
      acceptedConsentOnly?: boolean;
      runningAndActivatedOnly?: boolean;
      qSearch?: string;
      startDate?: Date;
      endDate?: Date;
    };
  };
  dTechRemoteAssistancesCompleted: {
    data: RemoteServiceListItemDto[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    };
    filters: {
      qSearch?: string;
      startDate?: Date;
      endDate?: Date;
    };
  };
  // --------------------------------
  // CUSTOMER D-TECH REMOTE
  // --------------------------------
  customerVehicles: {
    data: DiagnosticRemoteAssistanceVehicleDto[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    };
    filters: {
      qSearch?: string;
    };
  };
  dTechRemoteAssistancesCustomer: {
    data: DiagnosticRemoteAssistanceCustomerDto[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    };
    filters: {
      qSearch?: string;
      isCompleted?: boolean;
      startDate?: Date;
      endDate?: Date;
    };
  };
  status: any;
  key: string;
  customerConsent: RemoteServiceConsentDto | null;
}

export const initialState: State = {
  dTechRemoteAssistances: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    },
    filters: {
      runningOnly: false,
      connectedOnly: false,
      acceptedConsentOnly: false,
      runningAndActivatedOnly: false,
      qSearch: '',
      startDate: null,
      endDate: null,
    },
  },
  dTechRemoteAssistancesCompleted: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    },
    filters: {
      qSearch: '',
      startDate: null,
      endDate: null,
    },
  },
  customerVehicles: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    },
    filters: {
      qSearch: '',
    },
  },
  dTechRemoteAssistancesCustomer: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    },
    filters: {
      qSearch: '',
      isCompleted: false,
      startDate: null,
      endDate: null,
    },
  },
  status: {
    code: 'Loading',
    title: 'Loading',
    description: 'Waiting for the connection status',
    color: 'gray',
    icon: 'hourglass_bottom',
  },
  key: null,
  customerConsent: null,
};

export const selectDTechRemoteAssistances = state => state.dTechRemote.dTechRemoteAssistances;
export const selectDTechRemoteAssistancesCompleted = state => state.dTechRemote.dTechRemoteAssistancesCompleted;
export const selectCustomerVehicles = state => state.dTechRemote.customerVehicles;
export const selectDTechRemoteAssistancesCustomer = state => state.dTechRemote.dTechRemoteAssistancesCustomer;
export const selectAssistanceStatus = state => state.dTechRemote.status;
export const selectAssistanceKey = state => state.dTechRemote.key;
export const selectAssistanceConsent = state => state.dTechRemote.customerConsent;

export const dTechRemoteReducer = createReducer(
  initialState,
  on(RootActions.resetState, () => ({ ...initialState })),
  on(Actions.setDTechRemoteAssistancesLoading, (state, { isLoading }) => ({
    ...state, dTechRemoteAssistances: {
      ...state.dTechRemoteAssistances,
      isLoading,
    },
  })),
  on(Actions.setDTechRemoteAssistancesCompletedLoading, (state, { isLoading }) => ({
    ...state, dTechRemoteAssistancesCompleted: {
      ...state.dTechRemoteAssistancesCompleted,
      isLoading,
    },
  })),
  on(Actions.setDTechRemoteAssistancesFilters, (state, { filters }) => ({
    ...state, dTechRemoteAssistances: {
      ...state.dTechRemoteAssistances,
      filters,
    },
  })),
  on(Actions.setDTechRemoteAssistancesCompletedFilters, (state, { filters }) => ({
    ...state, dTechRemoteAssistancesCompleted: {
      ...state.dTechRemoteAssistancesCompleted,
      filters,
    },
  })),
  on(Actions.setDTechRemoteAssistancesPaginator, (state, { paginator }) => ({
    ...state, dTechRemoteAssistances: {
      ...state.dTechRemoteAssistances,
      paginator,
    },
  })),
  on(Actions.setDTechRemoteAssistancesCompletedPaginator, (state, { paginator }) => ({
    ...state, dTechRemoteAssistancesCompleted: {
      ...state.dTechRemoteAssistancesCompleted,
      paginator,
    },
  })),
  on(Actions.setDTechRemoteAssistancesList, (state, { response }) => ({
    ...state, dTechRemoteAssistances: {
      ...state.dTechRemoteAssistances,
      data: response.remoteServices,
      totalCount: response.total,
    },
  })),
  on(Actions.setDTechRemoteAssistancesCompletedList, (state, { response }) => ({
    ...state, dTechRemoteAssistancesCompleted: {
      ...state.dTechRemoteAssistancesCompleted,
      data: response.remoteServices,
      totalCount: response.total,
    },
  })),
  on(Actions.setDTechRemoteAssistanceStatus, (state, { status, key }) => {
    // If status.code is 'CtmConnected' or 'Loading' and key is null, keep the old key
    const shouldKeepKey = (status?.code === 'CtmConnected' || status?.code === 'Loading') && key == null;
    return {
      ...state,
      status,
      key: shouldKeepKey ? state.key : key,
    };
  }),
  on(Actions.setDTechRemoteAssistanceConsent, (state, { consent }) => ({
    ...state,
    customerConsent: consent,
  })),
  on(Actions.destroyWsDTechRemoteAssistanceStatus, (state) => ({
    ...state,
    customerConsent: null,
  })),
  on(Actions.setCustomerVehiclesLoading, (state, { isLoading }) => ({
    ...state, customerVehicles: {
      ...state.customerVehicles,
      isLoading,
    },
  })),
  on(Actions.setDTechRemoteAssistancesCustomerLoading, (state, { isLoading }) => ({
    ...state, dTechRemoteAssistancesCustomer: {
      ...state.dTechRemoteAssistancesCustomer,
      isLoading,
    },
  })),
  on(Actions.setCustomerVehiclesFilters, (state, { filters }) => ({
    ...state, customerVehicles: {
      ...state.customerVehicles,
      filters,
    },
  })),
  on(Actions.setDTechRemoteAssistancesCustomerFilters, (state, { filters }) => ({
    ...state, dTechRemoteAssistancesCustomer: {
      ...state.dTechRemoteAssistancesCustomer,
      filters,
    },
  })),
  on(Actions.setCustomerVehiclesPaginator, (state, { paginator }) => ({
    ...state, customerVehicles: {
      ...state.customerVehicles,
      paginator,
    },
  })),
  on(Actions.setDTechRemoteAssistancesCustomerPaginator, (state, { paginator }) => ({
    ...state, dTechRemoteAssistancesCustomer: {
      ...state.dTechRemoteAssistancesCustomer,
      paginator,
    },
  })),
  on(Actions.setCustomerVehiclesList, (state, { response }) => ({
    ...state, customerVehicles: {
      ...state.customerVehicles,
      data: response.vehicles,
      totalCount: response.total,
    },
  })),
  on(Actions.setDTechRemoteAssistancesCustomerList, (state, { response }) => ({
    ...state, dTechRemoteAssistancesCustomer: {
      ...state.dTechRemoteAssistancesCustomer,
      data: response.remoteAssistances,
      totalCount: response.total,
    },
  })),
);
